import { autoinject } from 'aurelia-framework';
import { HPFServiceClient } from '../../../services/hpfApi';
import { JsonSerializer } from '../../helpers/json-serializer';
import { AccountPaymentItem } from '../../models/account-payment-item';
import { ApiResponse } from '../../models/api-response';
import { PaymentItemTableConfiguration } from '../../models/payment-item-table-configuration';
import { ServiceResponse } from '../../models/service-response';
import { BaseService } from '../../services/base-service';
import { Logger } from '../../services/logger';
import { PaymentItemTableSettings } from './persistence/payment-item-table-settings';
import { PaymentItemDiscountBalanceRange } from '../../models/payment-item-discount-balance-range';
import { ObjectHelper } from '../../helpers/object-helper';

// there's currently a co-mingling of HPFServiceClient, HTTPInjector, and Portal's global state, with
// regard to handling the JWT. Ideally it would be decoupled or refactored in a such a way that it can
// be used independently of portal. For time's sake, just tie into the existing infrastructure for now.
@autoinject
export class PaymentItemService extends BaseService {
	constructor(
		private http: HPFServiceClient,
		protected logger: Logger,
		protected serializer: JsonSerializer
	) {
		super(logger, serializer);
	}

	getPaymentItemTableSettings(businessKey: number, customerAccountKeys: number[], forceRefreshOfData: boolean): Promise<ServiceResponse> {
		let settingsToReturn: PaymentItemTableSettings = this.loadSettings();

		// if not forcing info to be pulled from database, load from local storage
		if (!forceRefreshOfData) {
			return this.success('', settingsToReturn);
		}

		// if forcing refresh, grab from database
		let requestBody: any = { businessKey, customerAccountKeys } as IGetAccountPaymentItemsRequest;
		return this.http.post(`/customerAccounts/paymentItems`, requestBody)
			.then((apiResponse: ApiResponse) => {
				if (!apiResponse.isSuccessful) {
					return this.error(`${apiResponse.getErrorMessage()}`);
				}

				// handle global settings
				settingsToReturn.displayMinPaymentDue = apiResponse.value.displayMinPaymentDue;
				settingsToReturn.portalMinimumPaymentAmount = apiResponse.value.portalMinimumPaymentAmount;
				settingsToReturn.portalMaximumPaymentAmount = apiResponse.value.portalMaximumPaymentAmount;
				settingsToReturn.isBusinessBucketEnabled = apiResponse.value.isBusinessBucketEnabled;
				settingsToReturn.isShoppingCartEnabled = apiResponse.value.isShoppingCartEnabled;
				settingsToReturn.displayCustomerAccountName = apiResponse.value.displayCustomerAccountName;

				// handle items
				settingsToReturn.items = [];
				apiResponse.value.paymentItems.forEach((i: AccountPaymentItem) => {
					// set up account. Note some additional initialization is done here; not all properties are set from the response object
					let item: AccountPaymentItem = new AccountPaymentItem();
					item.itemKey = i.itemKey;
					item.paymentItemId = i.paymentItemId;
					item.isSelected = false;
					item.merchantKey = i.merchantKey;
					item.initialAmount = i.initialAmount;
					item.adjustedBalances = i.adjustedBalances;
					item.enteredAmount = i.initialAmount.toString();
					item.customerAccountReferenceId = i.customerAccountReferenceId;
					item.customerAccountName = i.customerAccountName;
					item.contactName = i.contactName;
					item.dueDate = (i.dueDate == null ? '' : i.dueDate);
					item.minPaymentDue = i.minPaymentDue;
					item.accountDescription = i.accountDescription;
					item.latestStatementDocumentKey = i.latestStatementDocumentKey;
					item.bucketDisplayName = i.bucketDisplayName;
					item.portfolioKey = i.portfolioKey;
					item.areFeesEnabled = i.areFeesEnabled;

					// set up payment limit settings
					item.paymentLimitSettings = ObjectHelper.deepCopyObject(apiResponse.value.paymentLimitSettings.find((x: any) => x.portfolioKey === item.portfolioKey));

					// set up balance ranges
					apiResponse.value.balanceRanges.forEach((x: any) => {
						if (x.portfolioKey === item.portfolioKey) {
							const range = new PaymentItemDiscountBalanceRange();
							range.min = x.min;
							range.max = x.max;
							range.payInFullDiscount = x.payInFullDiscountPercent;
							item.discountBalanceRanges.push(range);
							item.getDiscount();
						}
					});

					settingsToReturn.items.push(item);
				});

				this.saveSettings(settingsToReturn);
				return this.success('', settingsToReturn);
			});
	}

	//
	// functions to load and save from persistent storage
	//

	loadConfiguration(): PaymentItemTableConfiguration {
		return this.loadSettings().configuration;
	}

	saveConfiguration(configuration: PaymentItemTableConfiguration) {
		let settings: PaymentItemTableSettings = this.loadSettings();
		settings.configuration = configuration;
		this.saveSettings(settings);
	}

	loadBusinessKey(): number {
		return this.loadSettings().businessKey;
	}

	saveBusinessKey(businessKey: number) {
		let settings: PaymentItemTableSettings = this.loadSettings();
		settings.businessKey = businessKey;
		this.saveSettings(settings);
	}

	loadCustomerAccountKeys(): number[] {
		return this.loadSettings().customerAccountKeys;
	}

	saveCustomerAccountKeys(customerAccountKeys: number[]) {
		let settings: PaymentItemTableSettings = this.loadSettings();
		settings.customerAccountKeys = customerAccountKeys;
		this.saveSettings(settings);
	}

	loadForceRefreshOfData(): boolean {
		return this.loadSettings().forceRefreshOfData;
	}

	saveForceRefreshOfData(forceRefreshOfData: boolean) {
		let settings: PaymentItemTableSettings = this.loadSettings();
		settings.forceRefreshOfData = forceRefreshOfData;
		this.saveSettings(settings);
	}

	saveItems(items: AccountPaymentItem[]) {
		let settings: PaymentItemTableSettings = this.loadSettings();
		settings.items = items;
		this.saveSettings(settings);
	}

	private loadSettings(): PaymentItemTableSettings {
		let settings: PaymentItemTableSettings = this.getFromStorage('paymentItemTableSettings', PaymentItemTableSettings);
		return (settings !== null) ? settings : new PaymentItemTableSettings();
	}

	private saveSettings(settings: PaymentItemTableSettings): void {
		this.setInStorage('paymentItemTableSettings', settings);
	}
}

export interface IGetAccountPaymentItemsRequest {
	businessKey: number;
	customerAccountKeys: number[];
}
